import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import { ProjectAPI } from "../api/project";
import Modal from "./Modal";
import Button from "./Button";
import Select from "./Select";
import FileUpload from "./FileUpload";
import { FileInfoModal } from "./FileInfoModal";
import { buildSpecUploadNotification } from "../utils/specUploadResult";

const ModalContent = styled.div`
  padding: 2rem;
  max-width: 1200px;
  width: 100%;
`;

const ModalHeader = styled.div`
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const HelpText = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0.5rem 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const TableButton = styled(Button)`
  /* margin handled by parent gap */
  margin: 0;
`;

const FileInfoButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

interface ProjectSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    currentProject,
    projects,
    setCurrentProject,
    loadProjects,
    isLoading,
    error,
  } = useProject();
  const { addNotification } = useNotification();
  const [selectedProjectId, setSelectedProjectId] = useState(
    currentProject?.id || ""
  );
  const [projectRulesFile, setProjectRulesFile] = useState<File | null>(null);
  const [specsFile, setSpecsFile] = useState<File | null>(null);
  const [isUploadingRules, setIsUploadingRules] = useState(false);
  const [isDeletingRules, setIsDeletingRules] = useState(false);
  const [isUploadingSpecs, setIsUploadingSpecs] = useState(false);
  const [isDeletingSpecs, setIsDeletingSpecs] = useState(false);
  const [hasProjectRules, setHasProjectRules] = useState(false);
  const [hasSpecDocs, setHasSpecDocs] = useState(false);
  const [fileInfoModal, setFileInfoModal] = useState<{
    isOpen: boolean;
    type: "projectRules" | "specDocs";
  }>({
    isOpen: false,
    type: "projectRules",
  });
  const [isFileInfoModalLoading, setIsFileInfoModalLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const isRulesBusy = isUploadingRules || isDeletingRules;
  const isSpecsBusy = isUploadingSpecs || isDeletingSpecs;

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      setCurrentProject(project);
      addNotification({
        type: "success",
        title: "プロジェクト変更",
        message: `${project.name}に変更しました`,
      });
    }
  };

  const refreshFileExistence = async (projectId: string) => {
    try {
      const [rulesResp, specsResp] = await Promise.all([
        ProjectAPI.getProjectRulesInfo(projectId),
        ProjectAPI.getSpecificationDocsInfo(projectId),
      ]);

      // プロジェクトルールの有無
      if (rulesResp && rulesResp.success) {
        const data: any = rulesResp.data;
        if (data && typeof data.exists === "boolean") {
          setHasProjectRules(!!data.exists);
        } else {
          setHasProjectRules(!!data);
        }
      } else {
        setHasProjectRules(false);
      }

      // 仕様書ドキュメントの有無
      if (specsResp && specsResp.success) {
        let data: any = specsResp.data;
        if (Array.isArray(data)) {
          setHasSpecDocs(data.length > 0);
        } else if (data && Array.isArray(data.files)) {
          setHasSpecDocs(data.files.length > 0);
        } else if (data && Array.isArray(data.data)) {
          setHasSpecDocs(data.data.length > 0);
        } else {
          setHasSpecDocs(false);
        }
      } else {
        setHasSpecDocs(false);
      }
    } catch (e) {
      setHasProjectRules(false);
      setHasSpecDocs(false);
    }
  };

  useEffect(() => {
    if (selectedProjectId) {
      refreshFileExistence(selectedProjectId);
    } else {
      setHasProjectRules(false);
      setHasSpecDocs(false);
    }
  }, [selectedProjectId]);

  const handleProjectRulesUpload = async () => {
    if (!projectRulesFile || !selectedProjectId) return;

    setIsUploadingRules(true);
    try {
      const response = await ProjectAPI.uploadProjectRules(
        selectedProjectId,
        projectRulesFile
      );

      if (response.success) {
        addNotification({
          type: "success",
          title: "アップロード完了",
          message: "プロジェクトルールファイルをアップロードしました",
        });
        setProjectRulesFile(null);
        await refreshFileExistence(selectedProjectId);
      } else {
        throw new Error(response.error || "アップロードに失敗しました");
      }
    } catch (error) {
      addNotification({
        type: "error",
        title: "アップロード失敗",
        message:
          error instanceof Error
            ? error.message
            : "プロジェクトルールファイルのアップロードに失敗しました",
      });
    } finally {
      setIsUploadingRules(false);
    }
  };

  /** Cloud Run の HTTP/1 リクエストボディ上限 — ZIP単体 */
  const SPEC_SINGLE_ZIP_MAX_BYTES = 32 * 1024 * 1024;
  /** プロジェクト内の複数ZIPの合計サイズ上限 */
  const SPEC_COMBINED_MAX_BYTES = 256 * 1024 * 1024;

  const normalizeSpecUploadErrorMessage = (raw: unknown): string => {
    const rawMessage =
      raw instanceof Error ? raw.message : "仕様書ファイルのアップロードに失敗しました";
    const trimmed = String(rawMessage || "").trim();
    const candidate = trimmed.startsWith("AI Assist error:")
      ? trimmed.replace(/^AI Assist error:\s*/i, "")
      : trimmed;
    const unquoted = candidate.replace(/^['"]|['"]$/g, "");
    try {
      const parsed = JSON.parse(unquoted);
      if (parsed?.detail && typeof parsed.detail === "string") return parsed.detail;
      if (parsed?.message && typeof parsed.message === "string") return parsed.message;
    } catch {
      // JSON でない場合はそのまま使う
    }
    return candidate || "仕様書ファイルのアップロードに失敗しました";
  };

  const handleSpecsUpload = async () => {
    if (!specsFile || !selectedProjectId) return;

    // アップロードするZIP単体のサイズチェック
    if (specsFile.size > SPEC_SINGLE_ZIP_MAX_BYTES) {
      addNotification({
        type: "error",
        title: "アップロード失敗",
        message:
          "仕様書ファイルのアップロードが失敗しました。\nファイル容量が32MBを超えています。",
      });
      return;
    }

    // 複数ZIPの合計サイズチェック（256MB 上限）
    try {
      const specInfo = await ProjectAPI.getSpecificationDocsInfo(selectedProjectId);
      const payload = specInfo.data as
        | { total_spec_size_bytes?: number; data?: Array<{ source_zip_name?: string }> }
        | undefined;
      const currentTotalBytes =
        (payload && typeof payload.total_spec_size_bytes === "number"
          ? payload.total_spec_size_bytes
          : 0) ?? 0;
      if (currentTotalBytes + specsFile.size > SPEC_COMBINED_MAX_BYTES) {
        addNotification({
          type: "error",
          title: "アップロード失敗",
          message:
            "仕様書ファイルのアップロードが失敗しました。\n複数ZIPの合計が256MBを超えています。",
        });
        return;
      }

      // 同名ZIPの重複登録を禁止
      const existingNames = new Set(
        (payload?.data ?? [])
          .map((item) => (item?.source_zip_name ?? "").trim())
          .filter((name) => !!name)
      );
      if (existingNames.has((specsFile.name ?? "").trim())) {
        addNotification({
          type: "error",
          title: "アップロード失敗",
          message:
            "仕様書ファイルのアップロードが失敗しました。\n同じ名前のZIPファイルは既に登録されています。",
        });
        return;
      }
    } catch {
      // 取得失敗時はバックエンド側のチェックに委ねる
    }

    setIsUploadingSpecs(true);
    try {
      const response = await ProjectAPI.uploadSpecificationDocs(
        selectedProjectId,
        specsFile
      );

      if (response.success) {
        // 取り込めなかったファイルがあることを伝える。応答を見ずに成功通知だけを
        // 出していたため、検索対象外になったファイルに気付けなかった（#164）。
        addNotification(buildSpecUploadNotification(response.data));
        setSpecsFile(null);
        await refreshFileExistence(selectedProjectId);
      } else {
        throw new Error(response.error || "アップロードに失敗しました");
      }
    } catch (error) {
      const normalizedMessage = normalizeSpecUploadErrorMessage(error);
      const lower = normalizedMessage.toLowerCase();
      const isSingleZipTooLarge =
        normalizedMessage.includes("413") ||
        normalizedMessage.includes("Request failed with status code 413") ||
        normalizedMessage.includes("32MB") ||
        normalizedMessage.includes("ファイル容量が32MBを超えています");
      const isCombinedTooLarge =
        normalizedMessage.includes("256MB") ||
        normalizedMessage.includes("複数ZIPの合計");
      const isDuplicateZipName =
        normalizedMessage.includes("同じ名前のZIPファイルは既に登録されています") ||
        lower.includes("同じ名前のzipファイルは既に登録されています");
      addNotification({
        type: "error",
        title: "アップロード失敗",
        message: isSingleZipTooLarge
          ? "仕様書ファイルのアップロードが失敗しました。\nファイル容量が32MBを超えています。"
          : isCombinedTooLarge
            ? "仕様書ファイルのアップロードが失敗しました。\n複数ZIPの合計が256MBを超えています。"
            : isDuplicateZipName
              ? "仕様書ファイルのアップロードが失敗しました。\n同じ名前のZIPファイルは既に登録されています。"
              : normalizedMessage,
      });
    } finally {
      setIsUploadingSpecs(false);
    }
  };

  const handleDeleteProjectRules = async () => {
    if (!selectedProjectId) return;
    const confirmed = window.confirm(
      "プロジェクトルールファイルを削除します。よろしいですか？この操作は取り消せません。"
    );
    if (!confirmed) return;

    setIsDeletingRules(true);
    try {
      const response = await ProjectAPI.deleteProjectRules(selectedProjectId);

      if (response.success) {
        addNotification({
          type: "success",
          title: "削除完了",
          message: "プロジェクトルールファイルを削除しました",
        });
        await refreshFileExistence(selectedProjectId);
      } else {
        throw new Error(response.error || "削除に失敗しました");
      }
    } catch (error) {
      addNotification({
        type: "error",
        title: "削除失敗",
        message:
          error instanceof Error
            ? error.message
            : "プロジェクトルールファイルの削除に失敗しました",
      });
    } finally {
      setIsDeletingRules(false);
    }
  };

  const handleDeleteSpecs = async () => {
    if (!selectedProjectId) return;
    const confirmed = window.confirm(
      "仕様書ファイルを削除します。よろしいですか？この操作は取り消せません。"
    );
    if (!confirmed) return;

    setIsDeletingSpecs(true);
    try {
      const response = await ProjectAPI.deleteSpecificationDocs(
        selectedProjectId
      );

      if (response.success) {
        addNotification({
          type: "success",
          title: "削除完了",
          message: "仕様書ファイルを削除しました",
        });
        await refreshFileExistence(selectedProjectId);
      } else {
        throw new Error(response.error || "削除に失敗しました");
      }
    } catch (error) {
      addNotification({
        type: "error",
        title: "削除失敗",
        message:
          error instanceof Error
            ? error.message
            : "仕様書ファイルの削除に失敗しました",
      });
    } finally {
      setIsDeletingSpecs(false);
    }
  };

  const handleOpenTable = (type: "projectRules" | "specDocs") => {
    if (!selectedProjectId) {
      addNotification({
        type: "warning",
        title: "プロジェクトが未選択",
        message: "プロジェクトを選択してください",
      });
      return;
    }

    // If clicking the same button while modal is open, refresh data
    if (fileInfoModal.isOpen && fileInfoModal.type === type) {
      setIsFileInfoModalLoading(true);
      setRefreshTrigger(prev => prev + 1);
      return;
    }

    // Prevent rapid clicks while modal is loading
    if (isFileInfoModalLoading) {
      return;
    }

    setIsFileInfoModalLoading(true);
    setFileInfoModal({
      isOpen: true,
      type,
    });
  };

  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name,
  }));

  const disableRulesUpload = !projectRulesFile || isRulesBusy;
  const disableRulesDelete = isRulesBusy || !hasProjectRules;
  const disableSpecsUpload = !specsFile || isSpecsBusy;
  const disableSpecsDelete = isSpecsBusy || !hasSpecDocs;
  const disableFileInfoButtons = isFileInfoModalLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="1200px">
      <ModalContent>
        <ModalHeader>
          <ModalTitle>プロジェクト設定</ModalTitle>
        </ModalHeader>

        <Section>
          <FormGroup>
            <Label htmlFor="project-select">プロジェクトを選択</Label>
            {isLoading ? (
              <HelpText>プロジェクトを読み込み中...</HelpText>
            ) : error ? (
              <div>
                <HelpText style={{ color: "red" }}>
                  プロジェクトの読み込みに失敗しました: {error}
                </HelpText>
                <Button variant="secondary" onClick={loadProjects}>
                  再読み込み
                </Button>
              </div>
            ) : projects.length === 0 ? (
              <HelpText>利用可能なプロジェクトがありません</HelpText>
            ) : (
              <Select
                id="project-select"
                value={selectedProjectId}
                onChange={handleProjectChange}
                options={projectOptions}
                placeholder="プロジェクトを選択してください"
              />
            )}
          </FormGroup>
        </Section>

        {selectedProjectId && (
          <>
            <Section>
              <SectionTitle>プロジェクトルール</SectionTitle>
              <FormGroup>
                <Label htmlFor="project-rules-upload">
                  プロジェクトルールファイル
                </Label>
                <FileUpload
                  id="project-rules-upload"
                  accept=".txt"
                  file={projectRulesFile}
                  onChange={setProjectRulesFile}
                  disabled={isUploadingRules}
                  loading={isUploadingRules}
                  placeholder="txtファイルを選択してください"
                />
                <HelpText>.txtファイルのみアップロード可能です。</HelpText>
                <HelpText>
                  選択中のプロジェクトに対してルールが適用されます。
                </HelpText>
                <ButtonGroup>
                  <Button
                    onClick={handleProjectRulesUpload}
                    disabled={disableRulesUpload}
                    loading={isUploadingRules}
                  >
                    ルールをアップロード
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDeleteProjectRules}
                    disabled={disableRulesDelete}
                    loading={isDeletingRules}
                  >
                    ルールを削除する
                  </Button>
                </ButtonGroup>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>仕様検索用ドキュメント</SectionTitle>
              <FormGroup>
                <Label htmlFor="specs-upload">仕様書ファイル</Label>
                <FileUpload
                  id="specs-upload"
                  accept=".zip"
                  file={specsFile}
                  onChange={setSpecsFile}
                  disabled={isUploadingSpecs}
                  loading={isUploadingSpecs}
                  placeholder="zipファイルを選択してください"
                />
                <HelpText>.zipファイルのみアップロード可能です。</HelpText>
                <HelpText>
                  1つのZIPファイルは32MB以内、プロジェクト内の複数ZIPの合計は256MB以内です。
                </HelpText>
                <HelpText>
                  同じ名前のZIPファイルは重複アップロードできません。
                </HelpText>
                <HelpText>
                  選択中のプロジェクトに対して仕様検索用のドキュメントが適用されます。
                </HelpText>
                <ButtonGroup>
                  <Button
                    onClick={handleSpecsUpload}
                    disabled={disableSpecsUpload}
                    loading={isUploadingSpecs}
                  >
                    ドキュメントをアップロード
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDeleteSpecs}
                    disabled={disableSpecsDelete}
                    loading={isDeletingSpecs}
                  >
                    ドキュメントを削除する
                  </Button>
                </ButtonGroup>
              </FormGroup>
            </Section>
          </>
        )}

        <Section>
          <SectionTitle>ファイル情報</SectionTitle>
          <FileInfoButtonGroup>
            <TableButton
              variant="secondary"
              onClick={() => handleOpenTable("projectRules")}
              disabled={disableFileInfoButtons}
            >
              {isFileInfoModalLoading && fileInfoModal.type === "projectRules" 
                ? "読み込み中..." 
                : "プロジェクトルールファイルの情報を表示"}
            </TableButton>
            <TableButton
              variant="secondary"
              onClick={() => handleOpenTable("specDocs")}
              disabled={disableFileInfoButtons}
            >
              {isFileInfoModalLoading && fileInfoModal.type === "specDocs" 
                ? "読み込み中..." 
                : "仕様書ファイルの情報を表示"}
            </TableButton>
          </FileInfoButtonGroup>
        </Section>
      </ModalContent>

      <FileInfoModal
        isOpen={fileInfoModal.isOpen}
        onClose={() => {
          setFileInfoModal({ ...fileInfoModal, isOpen: false });
          setIsFileInfoModalLoading(false);
        }}
        type={fileInfoModal.type}
        projectId={selectedProjectId}
        onDataLoaded={() => setIsFileInfoModalLoading(false)}
        onSpecZipDeleted={
          selectedProjectId
            ? () => refreshFileExistence(selectedProjectId)
            : undefined
        }
        refreshTrigger={refreshTrigger}
      />
    </Modal>
  );
};

export default ProjectSettings;
